/*
 * voice.js — Utilidades sobre la Web Speech API nativa del navegador.
 *
 * - speak(text): usa SpeechSynthesis para leer en voz alta la respuesta de la IA.
 *   Preferimos una voz en español si está disponible.
 * - createRecognizer(): devuelve una instancia de SpeechRecognition (o webkit)
 *   con handlers listos para 'onresult' / 'onerror' / 'onend'. Retorna null si
 *   el navegador no soporta STT (Firefox móvil, algunos WebViews).
 *
 * Ventajas de usar la API nativa:
 * - Cero llamadas al backend → sin costo por minuto de TTS/STT.
 * - Latencia ~0 (funciona offline en muchos dispositivos).
 * - Cumplimiento de privacidad: el audio del micrófono ni siquiera sale del dispositivo
 *   en Chrome desktop (usa la nube en Chrome mobile, pero el usuario ya consintió).
 */

import { devWarn } from "./devLog";

export function speak(text, opts = {}) {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  try {
    // Cancela cualquier locución anterior antes de arrancar la nueva
    window.speechSynthesis.cancel();
    const utter = new window.SpeechSynthesisUtterance(text);
    // Elegir voz en español si existe
    const voices = window.speechSynthesis.getVoices();
    const esVoice = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith("es"));
    if (esVoice) utter.voice = esVoice;
    utter.lang = esVoice?.lang || "es-MX";
    utter.rate = opts.rate ?? 1.05;
    utter.pitch = opts.pitch ?? 1.0;
    utter.volume = opts.volume ?? 1.0;
    window.speechSynthesis.speak(utter);
    return utter;
  } catch (err) {
    // SpeechSynthesis puede fallar en algunos WebViews / Safari con audio bloqueado.
    // No es fatal — el usuario simplemente no oirá la respuesta.
    devWarn("[voice.speak] SpeechSynthesis failed:", err);
  }
}

export function stopSpeaking() {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
}

export function isTTSAvailable() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

export function createRecognizer({ onResult, onError, onEnd, lang = "es-MX" } = {}) {
  if (typeof window === "undefined") return null;
  const Ctor =
    window.SpeechRecognition ||
    window.webkitSpeechRecognition;
  if (!Ctor) return null;
  const rec = new Ctor();
  rec.lang = lang;
  rec.continuous = false;
  rec.interimResults = true;
  rec.maxAlternatives = 1;

  rec.onresult = (e) => {
    let interim = "";
    let finalText = "";
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i];
      if (r.isFinal) finalText += r[0].transcript;
      else interim += r[0].transcript;
    }
    onResult?.({ interim, final: finalText });
  };
  rec.onerror = (e) => onError?.(e?.error || "recognition-error");
  rec.onend = () => onEnd?.();
  return rec;
}

export function isSTTAvailable() {
  if (typeof window === "undefined") return false;
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}
